import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { segmentsService } from '../services/segments.service';
import type {
  CreateSegmentPayload,
  UpdateSegmentPayload,
  DeleteSegmentPayload,
} from '../types/api.types';
import toast from 'react-hot-toast';

// Query key factory
export const segmentKeys = {
  all: ['segments'] as const,
  byEpisode: (projectId: number, episodeId: number) =>
    [...segmentKeys.all, projectId, episodeId] as const,
};

// Get segments for a project and episode
export function useSegments(projectId: number, episodeId: number) {
  return useQuery({
    queryKey: segmentKeys.byEpisode(projectId, episodeId),
    queryFn: () => segmentsService.getSegments(projectId, episodeId),
    enabled: !!projectId && !!episodeId,
    staleTime: 30000, // Consider data fresh for 30 seconds to prevent excessive refetching
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}

// Create a new segment
export function useCreateSegment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateSegmentPayload) =>
      segmentsService.createSegment(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: segmentKeys.byEpisode(variables.projectId, variables.episodeId),
      });
      toast.success('Segment saved successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to save segment: ${error.message}`);
    },
  });
}

// Update an existing segment
export function useUpdateSegment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateSegmentPayload) =>
      segmentsService.updateSegment(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: segmentKeys.byEpisode(variables.projectId, variables.episodeId),
      });
      toast.success('Segment updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update segment: ${error.message}`);
    },
  });
}

// Delete a segment
export function useDeleteSegment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: DeleteSegmentPayload) =>
      segmentsService.deleteSegment(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: segmentKeys.byEpisode(variables.projectId, variables.episodeId),
      });
      toast.success('Segment deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete segment: ${error.message}`);
    },
  });
}
